/**
 * analytics-shared/engine/reportEngine.js
 *
 * Display-only helpers for the analytics UI. After wedge 5 the JS report
 * engine is **gone**: all execution (filtering, joining, grouping,
 * aggregation, chart-data construction) lives server-side in
 * backend/modules/analytics_engine. This file keeps the small set of
 * functions that are still legitimately client-side:
 *
 *   formatValue / formatAggValue   — locale-aware cell formatting
 *   monthLabel                     — date bucket label for chart axes
 *   flattenTree                    — view-state flattener for the table
 *                                    widget (turns the engine's grouped
 *                                    tree + the user's expand/collapse
 *                                    state into a row list)
 *   AGGREGATIONS / OPERATORS /
 *   DATE_PRESETS / getOperators /
 *   countConditions                — UI constants + helpers for the
 *                                    builder dropdowns and filter
 *                                    summary counts. Pure metadata,
 *                                    not execution.
 *
 * Anything else that used to live here — buildReport, buildChartData,
 * applyFilters, aggregate — has been removed. Calls to those should
 * go through the server (`POST /v1/reports/run`, `/widgets/run`,
 * `/reports/:id/run`). The fixture-only demo path keeps a private port
 * inside reports/api/fixtureRunner.js — that file is the only consumer
 * of the legacy logic and it's dynamically imported, so the production
 * bundle does not include it.
 */

export const AGGREGATIONS = [
  { value: 'SUM', label: 'Sum' },
  { value: 'AVG', label: 'Average' },
  { value: 'MIN', label: 'Min' },
  { value: 'MAX', label: 'Max' },
  { value: 'COUNT', label: 'Count' },
];

const NUMERIC_OPS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'between', label: 'between' },
];

// Dynamic token: "the user running the report". Resolved server-side so
// one shared report shows each viewer their own records.
export const ME_TOKEN = '__me__';

const MULTI_VALUE_OPS = [
  { value: 'includes_any', label: 'includes any of', multi: true },
  { value: 'includes_all', label: 'includes all of', multi: true },
  { value: 'excludes', label: 'excludes', multi: true },
  { value: 'is_empty', label: 'is empty', valueless: true },
  { value: 'is_not_empty', label: 'is not empty', valueless: true },
];

export const OPERATORS = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'is_empty', label: 'is empty', valueless: true },
    { value: 'is_not_empty', label: 'is not empty', valueless: true },
  ],
  checkbox: [
    { value: 'equals', label: 'is', boolean: true },
  ],
  number: NUMERIC_OPS,
  currency: NUMERIC_OPS,
  percent: NUMERIC_OPS,
  select: [
    { value: 'is_one_of', label: 'is any of', multi: true },
    { value: 'is_none_of', label: 'is none of', multi: true },
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'is_empty', label: 'is empty', valueless: true },
    { value: 'is_not_empty', label: 'is not empty', valueless: true },
  ],
  user: [
    { value: 'is_one_of', label: 'is any of', multi: true, lookup: 'user' },
    { value: 'is_none_of', label: 'is none of', multi: true, lookup: 'user' },
    { value: 'is_empty', label: 'is empty', valueless: true },
    { value: 'is_not_empty', label: 'is not empty', valueless: true },
    // Legacy operators kept so pre-rebuild saved conditions render the
    // operator they actually use instead of silently displaying the
    // first option.
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
  ],
  lookup: [
    { value: 'is_one_of', label: 'is any of', multi: true, lookup: 'record' },
    { value: 'is_none_of', label: 'is none of', multi: true, lookup: 'record' },
    { value: 'is_empty', label: 'is empty', valueless: true },
    { value: 'is_not_empty', label: 'is not empty', valueless: true },
  ],
  tags: MULTI_VALUE_OPS,
  multiselect: MULTI_VALUE_OPS,
  date: [
    { value: 'date_preset', label: 'in range', preset: true },
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
    { value: 'between', label: 'between' },
    { value: 'is_empty', label: 'is empty', valueless: true },
    { value: 'is_not_empty', label: 'is not empty', valueless: true },
  ],
};

// Named calendar presets (grouped for the dropdown), matching the
// backend's _preset_range vocabulary. Rolling windows ("Last N days")
// are built by the RelativeDateEditor as "<kind>:<n>" tokens.
export const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_week_so_far', label: 'This Week to Date' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'next_week', label: 'Next Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_month_so_far', label: 'This Month to Date' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'next_month', label: 'Next Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_quarter_so_far', label: 'This Quarter to Date' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'next_quarter', label: 'Next Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'this_year_so_far', label: 'This Year to Date' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'next_year', label: 'Next Year' },
];

// Rolling-window kinds for the "Custom rolling" date editor.
export const ROLLING_KINDS = [
  { value: 'last_n_days', label: 'Last N days' },
  { value: 'next_n_days', label: 'Next N days' },
  { value: 'last_n_weeks', label: 'Last N weeks' },
  { value: 'next_n_weeks', label: 'Next N weeks' },
  { value: 'last_n_months', label: 'Last N months' },
  { value: 'next_n_months', label: 'Next N months' },
  { value: 'more_than_days_ago', label: 'More than N days ago' },
  { value: 'more_than_days_ahead', label: 'More than N days from now' },
];

// Report scope — Salesforce's "Show Me" standard filter.
export const SCOPES = [
  { value: 'my', label: 'My records' },
  { value: 'all', label: 'All records' },
];

// Field types whose stored value is a list (tags / multi-select).
const MULTI_VALUE_TYPES = new Set(['tags', 'tag', 'multiselect', 'multi_select', 'multipicklist', 'multi-select']);

export const getOperators = (type) => {
  if (MULTI_VALUE_TYPES.has(type)) return OPERATORS.tags;
  return OPERATORS[type] || OPERATORS.text;
};

// Human label for a date preset / rolling token — used by filter chips.
export const presetLabel = (value) => {
  const named = DATE_PRESETS.find((p) => p.value === value);
  if (named) return named.label;
  if (typeof value === 'string' && value.includes(':')) {
    const [kind, n] = value.split(':');
    const k = ROLLING_KINDS.find((r) => r.value === kind);
    if (k) return k.label.replace('N', n);
  }
  return String(value || '');
};

/**
 * Walk a filter tree and return the count of leaf conditions. Used by
 * the filter chip in the builder ("3 filters applied"). No execution —
 * pure structural traversal.
 */
export function countConditions(group) {
  if (!group || !group.children) return 0;
  return group.children.reduce(
    (n, c) => n + (c.type === 'group' ? countConditions(c) : 1),
    0,
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'Jan 2026' label for date bucket headers in chart legends. Used purely
 * for display — the actual date-to-bucket mapping happens on the server.
 */
export function monthLabel(isoDate) {
  if (!isoDate) return 'Unknown';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Flatten the engine's grouped tree into a row list for the table
 * widget. Walks the tree, honouring the user's expand-set, and emits
 * `{ kind: 'group', node }` and `{ kind: 'record', record, level }`
 * rows. Pure view-state — no aggregation.
 */
export function flattenTree(tree, expanded, showRecords) {
  const rows = [];
  const walk = (nodes) => {
    nodes.forEach((node) => {
      rows.push({ kind: 'group', node });
      if (expanded.has(node.path)) {
        if (node.children) walk(node.children);
        else if (showRecords && node.records) {
          node.records.forEach((rec) =>
            rows.push({ kind: 'record', record: rec, level: node.level + 1 }));
        }
      }
    });
  };
  walk(tree || []);
  return rows;
}

/**
 * Salesforce-style qualified label for a field. Primary-object fields
 * render bare ("City"); related fields get the source-object name in
 * parens ("City (Lead)"). Disambiguates same-named fields across
 * Account / Lead / Contact / Opportunity / etc. in chips, headers,
 * chart axis labels, filter chips, and tooltips.
 *
 * Source of truth:
 *   1. `field.display_label` if the backend already computed one
 *      (multi-object report types via /v1/report-types/fields, and the
 *      runner-side _load_field_map).
 *   2. Otherwise: derive locally from `field.object_key` /
 *      `field.is_primary_object`. Single-object mode (no object_key)
 *      falls through to the plain label — same UX as before the wedge.
 */
export function displayLabel(field) {
  if (!field) return '';
  if (field.display_label) return field.display_label;
  const base = field.label || field.key || '';
  if (!base) return '';
  if (field.is_primary_object) return base;
  if (field.object_key) {
    const objLabel = field.object_label || field.object_key;
    return `${base} (${objLabel})`;
  }
  return base;
}

/**
 * Locale-aware cell formatter. Numbers get thousands separators;
 * currency gets a $ prefix; dates get a "Mon D, YYYY" rendering.
 * Returns "—" for empty values so cells never look broken.
 */
export function formatValue(value, field) {
  if (value == null || value === '') return '—';
  const type = field ? field.type : undefined;
  if (type === 'currency') return `$${Number(value).toLocaleString('en-US')}`;
  if (type === 'percent') return `${value}%`;
  if (type === 'number') return Number(value).toLocaleString('en-US');
  if (type === 'date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(value);
}

/**
 * Aggregate-value formatter — COUNT values always render as integers
 * with thousands separators regardless of the underlying field type,
 * everything else falls back to the field's normal formatting.
 */
export function formatAggValue(value, fieldKey, aggType, fieldMap) {
  if (aggType === 'COUNT') return Number(value).toLocaleString('en-US');
  return formatValue(value, (fieldMap || {})[fieldKey]);
}
